import { useLayoutEffect } from "react";

/** The admin shells' canvas: Tailwind gray-100, the `bg-gray-100` of AdminLayout and MainApp. */
export const ADMIN_CANVAS = "#f3f4f6";

/** index.html's theme-color meta, or a new one when the page has none. */
function themeColorMeta(): { meta: HTMLMetaElement; added: boolean } {
  const existing = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (existing) return { meta: existing, added: false };
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  document.head.appendChild(meta);
  return { meta, added: true };
}

/**
 * Paints what surrounds the app in `color` while the calling component is
 * mounted (spec C3): the theme-color meta, which colours the iPhone's
 * status-bar strip, and the html and body backgrounds, which show past the
 * app under the home indicator. On unmount it puts back exactly what was
 * there, so the client portal keeps index.html's gray-50. A layout effect,
 * so the first paint already has the colour; React runs a removed shell's
 * cleanup before the next shell's effect in the same commit, so moving
 * between the two admin shells never paints the portal's colour.
 */
export function useCanvasColor(color: string): void {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const { meta, added } = themeColorMeta();
    const previous = { theme: meta.content, html: html.style.backgroundColor, body: body.style.backgroundColor };
    meta.content = color;
    html.style.backgroundColor = color;
    body.style.backgroundColor = color;
    return () => {
      html.style.backgroundColor = previous.html;
      body.style.backgroundColor = previous.body;
      if (added) meta.remove();
      else meta.content = previous.theme;
    };
  }, [color]);
}
